<template>
    <ProfileShareBackNotification v-if="canPromptShareBack" :text="bannerText" :disabled="isRefreshing" data-testid="share-back-banner" @share="startShareBack" />
    <BaseNotification v-if="isReadyToShare" theme="primary" hide-dismiss data-testid="share-back-ready">
        <template #content>
            <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6">
                <div class="flex flex-col gap-1">
                    <p class="body">Your profile is ready. Share it back with {{ shareRecipientName }}?</p>
                    <p class="body-small">{{ sharingDisclosure }}</p>
                    <p v-if="profile?.access === ACCESS.PRIVATE" class="body-small">{{ privateSharingHelp }}</p>
                </div>
                <BaseButton theme="secondary" class="shrink-0" :disabled="isRefreshing || profile?.is_flagged || !profileReady" @click="startShareBack">Share</BaseButton>
            </div>
        </template>
    </BaseNotification>
    <BaseNotification v-if="error" theme="error" :text="error" @dismiss="error = null" />

    <BaseDialog
        v-model:open="isLinkedOpen"
        :title="landingTitle"
        title-style="serif"
        :width="profile?.access === ACCESS.PRIVATE ? 'default' : 'wide'"
        :mobile-height="profile?.access === ACCESS.PRIVATE ? 'standard' : 'expanded'"
        :reserve-mobile-nav-space="false"
        @update:open="onLinkedOpenChange">
        <div v-if="profile?.access === ACCESS.PRIVATE" class="flex flex-col items-center gap-4">
            <div class="flex items-center gap-3" aria-hidden="true">
                <CollaborationProfileAvatar size="lg" :name="target?.name" />
                <i class="bi bi-arrow-left-right text-ink-muted" aria-hidden="true" />
                <CollaborationProfileAvatar size="lg" :name="creation.user_basic.full_name" />
            </div>
            <div class="flex flex-col gap-3 text-center">
                <p class="body-small text-ink-muted">Nadia will use {{ targetFirstName }}'s profile to coach you on working together.</p>
                <p class="body-small text-ink-muted">Enable sharing so your profile can be shared with {{ targetFirstName }} and other colleagues.{{ ` ${sharingDisclosure}` }}</p>
            </div>
        </div>
        <div v-else class="flex flex-col gap-4">
            <p class="scaffold-body-small md:scaffold-body text-ink-muted">{{ profile ? "Share your collaboration profile" : "Create your collaboration profile" }}</p>
            <p class="body-small md:body text-ink-muted">{{ landingDescription }}</p>
            <ProfileShareBackComparison :sender-name="target?.name ?? ''" :viewer-name="creation.user_basic.full_name" />
        </div>
        <template #footer>
            <template v-if="profile?.access === ACCESS.PRIVATE">
                <BaseButton theme="tertiary" size="sm" @click="dismissLinked">Maybe later</BaseButton>
                <BaseButton theme="primary" :disabled="isRefreshing" @click="chooseShareBack">Enable sharing</BaseButton>
            </template>
            <BaseButton v-else theme="primary" :disabled="isRefreshing" @click="chooseShareBack">{{ landingAction }}</BaseButton>
        </template>
    </BaseDialog>

    <ShareProfileDialog
        v-if="isSharingOpen && profile && !profile.is_flagged"
        v-model:open="isSharingOpen"
        :existing-public-profile="profile"
        :available-access-levels="sharingAccessLevels"
        :org-has-ms-teams="orgHasMsTeams"
        :org-has-slack="orgHasSlack"
        :own-profile-url="ownProfileUrl"
        :recipient-name="shareRecipientName"
        share-source="share_back"
        @shared="onShared" />
    <BaseToast v-model:open="isCopiedToastOpen" description="Profile link copied. Send it to your colleague to share back." :truncate-length="200" />
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { profileShareBackMessage, type ProfileShareTarget, useProfileShareActions } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { type ShareBackTarget, useProfileShareBack, withShareBackTarget } from "~vue/CollaborationProfile/composables/useProfileShareBack";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import { logProfileShared } from "~vue/CollaborationProfile/profileShareAttribution";
import ProfileShareBackComparison from "~vue/CollaborationProfile/ProfileShareBackComparison.vue";
import ProfileShareBackNotification from "~vue/CollaborationProfile/ProfileShareBackNotification.vue";
import ShareProfileDialog from "~vue/CollaborationProfile/ShareProfileDialog.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import type { ExistingPublicProfile, ProfileCreationProps } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { ACCESS } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { firstNameOf } from "~vue/utils/stringUtils";
import { computed, ref, watch } from "vue";

const { canComparePerspectives, sharingDisclosure } = useProfileSharingCopy();

const props = defineProps<{
    creation: ProfileCreationProps;
    profile: ExistingPublicProfile | null;
    target?: ShareBackTarget;
    pendingTarget?: ShareBackTarget | null;
    isSelf: boolean;
    linked: boolean;
    linkedByTarget?: boolean;
    preview?: boolean;
    profileReady: boolean;
    availableAccessLevels: string[];
    orgHasMsTeams: boolean;
    orgHasSlack: boolean;
}>();
const emit = defineEmits<{ finished: [] }>();
const { hasAnsweredPrompt, markPromptAnswered } = useProfileShareBack(props.creation.user_basic.uuid);
const isLinkedOpen = ref(false);
const isSharingOpen = ref(false);
const isRefreshing = ref(false);
const isCopiedToastOpen = ref(false);
const error = ref<string | null>(null);

const sharingAccessLevels = computed(() => new Set(props.availableAccessLevels));
const targetFirstName = computed(() => firstNameOf(props.target?.name ?? "your colleague"));
const landingTitle = computed(() => {
    if (props.profile?.access === ACCESS.PRIVATE) {
        return `You linked ${props.target?.name}`;
    }
    return props.profile ? `You linked ${props.target?.name} but haven’t shared yours` : "Right now Nadia is guessing how you like to work";
});
const landingDescription = computed(() => {
    if (canComparePerspectives.value) {
        const action = props.profile ? "Share your profile back" : "Create and share your profile";
        return `${action} so Nadia can help ${targetFirstName.value} work better with you, too. ${sharingDisclosure} When you both have Perspective results, you can compare your working styles.`;
    }
    if (props.profile) {
        return `You can see how ${targetFirstName.value} likes to work, and Nadia can coach you with that context in mind. Share yours back so Nadia can help ${targetFirstName.value} work better with you, too. ${sharingDisclosure}`;
    }
    if (props.creation.user_basic.has_perspective_report) {
        return `You can see how ${targetFirstName.value} likes to work, and Nadia can coach you with that context in mind. Create your profile from your Perspective, then you can share it with ${targetFirstName.value}. ${sharingDisclosure}`;
    }
    if (!props.creation.perspective_assessment_enabled) {
        return `You can see how ${targetFirstName.value} likes to work, and Nadia can coach you with that context in mind. Create your profile, then you can share it with ${targetFirstName.value}. ${sharingDisclosure}`;
    }
    return `You can see how ${targetFirstName.value} likes to work. Right now, teammates asking about you get Nadia’s best guess. Take a short quiz on your working style, then Nadia will draft your profile for you to review and share when you’re ready. ${sharingDisclosure}`;
});
const landingAction = computed(() => (props.profile ? "Share my profile" : props.creation.user_basic.has_perspective_report ? "Create my profile" : "Get started"));
const shareRecipientName = computed(() => firstNameOf((props.isSelf ? props.pendingTarget?.name : props.target?.name) ?? "your colleague"));
const ownProfileUrl = computed(() => new URL(props.creation.own_profile_url, globalThis.location.origin).href);
const profileAccess = computed(() => props.profile?.access ?? ACCESS.PRIVATE);
const { copyLink, shareViaTeams } = useProfileShareActions({
    profileId: computed(() => props.profile?.id),
    access: profileAccess,
    ownProfileUrl,
    shareSource: "share_back",
});
const canPromptShareBack = computed(() => !props.isSelf && props.linked && !!props.target && !props.linkedByTarget);
const isReadyToShare = computed(() => props.isSelf && !!props.profile && !!props.pendingTarget);
const privateSharingHelp = computed(() =>
    props.availableAccessLevels.includes(ACCESS.ORGANIZATION)
        ? `Your profile is private. Enable organization sharing so ${shareRecipientName.value} and others in your organization can see it.`
        : `Your profile is private. Enable invite-only sharing so ${shareRecipientName.value} can see it when they open your link.`,
);
const bannerText = computed(() => {
    if (!props.profile) {
        return `Create your collaboration profile to share it back with ${targetFirstName.value}. ${sharingDisclosure}`;
    }
    if (props.profile.access === ACCESS.PRIVATE) {
        return `${privateSharingHelp.value} ${sharingDisclosure}`;
    }
    return `${targetFirstName.value} can't see how you work yet. ${sharingDisclosure}`;
});

watch(
    () => [canPromptShareBack.value, props.preview],
    () => {
        if (canPromptShareBack.value && props.target && (props.preview || !hasAnsweredPrompt(props.target.id))) {
            isLinkedOpen.value = true;
            void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_PROMPT_SHOWN, { has_profile: !!props.profile });
        }
    },
    { immediate: true },
);

const onLinkedOpenChange = (open: boolean) => {
    if (!open) {
        void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_DISMISSED);
    }
};
const dismissLinked = () => {
    if (props.target) {
        markPromptAnswered(props.target.id);
    }
    isLinkedOpen.value = false;
    onLinkedOpenChange(false);
};

const chooseShareBack = () => {
    if (props.target) {
        markPromptAnswered(props.target.id);
    }
    void startShareBack();
};

const startShareBack = async () => {
    if (isRefreshing.value) {
        return;
    }
    error.value = null;
    isRefreshing.value = true;
    let enabled = false;
    await new Promise<void>((resolve) =>
        router.reload({
            only: ["existing_public_profile", "viewer_public_profile", "profile_creation", "available_access_levels", "share_back_enabled", "org_has_ms_teams"],
            onSuccess: (page) => {
                enabled = page.props.share_back_enabled === true;
            },
            onFinish: () => resolve(),
        }),
    );
    try {
        if (!enabled) {
            error.value = "Profile sharing is no longer available. Please refresh the page.";
            return;
        }
        isLinkedOpen.value = false;
        void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_CLICKED, { has_profile: !!props.profile });
        if (!props.profile && props.target) {
            void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_CREATION_STARTED, {
                has_perspective: !!props.creation.user_basic.has_perspective_report,
            });
            if (!props.creation.perspective_assessment_enabled) {
                router.visit(withShareBackTarget("/profile/create", props.target.id));
            } else if (props.creation.user_basic.has_perspective_report) {
                router.visit(withShareBackTarget("/profile/create?auto_generate=true", props.target.id));
            } else {
                router.visit(withShareBackTarget("/perspective/start/?source=profile_share_back", props.target.id));
            }
        } else if (props.profile?.is_flagged) {
            error.value = "Your profile is under review. You can share it once it is approved.";
        } else if (profileAccess.value === ACCESS.PRIVATE) {
            isSharingOpen.value = true;
        } else {
            const message = profileShareBackMessage(shareRecipientName.value);
            const target = props.orgHasMsTeams ? "teams" : "link";
            const shared = props.orgHasMsTeams ? await shareViaTeams(message) : await copyLink(message);
            if (shared) {
                void logProfileShared({ shareSource: "share_back", target, shareKind: "own_profile", access: profileAccess.value });
                onShared(target);
            } else {
                error.value = props.orgHasMsTeams ? "We couldn't open your Teams message. Please try again." : "We couldn't copy your profile link. Please try again.";
            }
        }
    } finally {
        isRefreshing.value = false;
    }
};

const onShared = (target: ProfileShareTarget) => {
    if (target === "link") {
        void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_COPIED);
    }
    if (props.isSelf) {
        emit("finished");
    }
    isSharingOpen.value = false;
    isCopiedToastOpen.value = target === "link";
};
</script>
